/**
 * One misc. line — a description, a quantity, a price. No IMEI, no stock warning, no wholesale
 * tier: none of that applies to a line with no catalogue variant behind it. Unlike a catalogue
 * line, this one is not edited in place — remove it and add it again with the right numbers,
 * since a misc. line is rare enough that a second tap is not a real cost.
 */

import { Money, cn } from '@/shared/ui';

import type { GenericCartLine } from '../model/cartSlice.js';

export type GenericLineRowProps = {
  line: GenericCartLine;
  onRemove: (line: GenericCartLine) => void;
};

export function GenericLineRow({ line, onRemove }: GenericLineRowProps) {
  return (
    <div className={cn('rounded-card bg-surface-raised p-3 shadow-sm')}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold leading-tight text-content-primary">
            {line.description}
          </p>
          <p className="mt-1 text-xs text-content-muted">Not from the product list</p>
        </div>

        <button
          type="button"
          aria-label={`Remove ${line.description}`}
          onClick={() => {
            onRemove(line);
          }}
          className="-mr-1 -mt-1 rounded-full p-2 text-content-muted transition-colors hover:bg-surface-sunken"
        >
          <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="m4 4 8 8m0-8-8 8"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div className="mt-3 flex items-center justify-end gap-2">
        <Money kes={line.unitPriceKes} form="bare" />
        <span className="text-content-muted">×{line.quantity} =</span>
        <Money kes={line.unitPriceKes * line.quantity} size="lg" form="bare" />
      </div>
    </div>
  );
}
